'use client';

import SchedulerTab from '@/components/SchedulerTab';
import DashboardKPI from '@/components/DashboardKPI';
import Panel from '@/components/Panel';
import DslmInsightPanel from '@/components/dslm-insight/DslmInsightPanel';

import { useState, useEffect, useRef, useCallback } from 'react';
import Link from 'next/link';
import { useTheme } from '@/components/ThemeContext';
import { PipelineInbox } from '@/components/PipelineInbox';
import {
    Radio, Search, Bot, Coins, FolderSearch, BarChart3, CalendarDays, Wallet,
    Sun, Moon, Maximize2, Minimize2, Handshake, MessageSquareText,
    Monitor, ChevronRight, ChevronDown, FileText, FolderOpen, Folder,
    Terminal, Database, Zap, Sparkles, Settings2, FileArchive, FileImage, FileLineChart, FileBox, FileVideo, ShieldAlert, CheckCircle2, Network, Activity,
    Lock, User, X as XIcon, Keyboard, Paperclip, Files, Ruler, Tag, ThumbsDown, ThumbsUp,
    type LucideIcon,
} from 'lucide-react';
import {
    AgentEvent, NodeHealth, HealthData, ChatMessage, SearchResult,
    WorkerState, EstimateItem, NasItem, FilePreview, ExplorerChat,
    TreeNode, AIEstimate, AGENTS_MAP, MAX_EVENTS,
    MAX_TERMINAL, TABS,
} from './types';
import { useExplorer } from './hooks/useExplorer';

const TAB_ICONS: Record<string, LucideIcon> = {
    radio: Radio, search: Search, bot: Bot, coins: Coins, wallet: Wallet,
    'folder-search': FolderSearch, 'bar-chart-3': BarChart3, 'calendar-days': CalendarDays,
    sparkles: Sparkles,
};

/** 파일 확장자에 따른 아이콘 반환 */
const fileIcon = (ext?: string, type?: string): string => {
 if (type === 'dir') return '';
    const e = (ext || '').toLowerCase().replace('.', '');
    const map: Record<string, string> = {
 pdf: '', doc: '', docx: '', hwp: '', hwpx: '',
 xls: '', xlsx: '', csv: '',
 ppt: '', pptx: '',
 dwg: '', dxf: '',
 jpg: '', jpeg: '', png: '', gif: '', bmp: '', tiff: '', psd: '', svg: '',
 skp: '', rvt: '', ifc: '', obj: '', stl: '', fbx: '', '3ds': '',
 zip: '', rar: '', '7z': '', tar: '', gz: '',
 mp4: '', mov: '', avi: '', mkv: '',
 mp3: '', wav: '', flac: '',
 txt: '', md: '', json: '', xml: '', yaml: '', yml: '',
 js: '', ts: '', py: '', html: '', css: '',
    };
 return map[e] || '';
};

// ── Main Component ─────────────────────────────────────
export default function LivePage() {
    const [activeTab, setActiveTab] = useState('rag-explorer');
    const [showExplorer, setShowExplorer] = useState(true);
    const [showAgent, setShowAgent] = useState(true);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const { theme, toggleTheme } = useTheme();
    const isDark = theme === 'dark';

    // Native Fullscreen API
    const toggleFullscreen = useCallback(() => {
        if (!document.fullscreenElement) {
            document.documentElement.requestFullscreen().catch(() => { });
        } else {
            document.exitFullscreen().catch(() => { });
        }
    }, []);

    useEffect(() => {
        const handler = () => setIsFullscreen(!!document.fullscreenElement);
        document.addEventListener('fullscreenchange', handler);
        return () => document.removeEventListener('fullscreenchange', handler);
    }, []);

    // Stream state
    const [connected, setConnected] = useState(false);
    const [lastObserved, setLastObserved] = useState<string | null>(null);
    const seenActivities = useRef(new Set<string>());
    const [events, setEvents] = useState<AgentEvent[]>([]);
    const [nodeHealth, setNodeHealth] = useState<HealthData | null>(null);
    const [agentCounts, setAgentCounts] = useState<Record<string, number>>({});
    const [totalEvents, setTotalEvents] = useState(0);
    const [filter, setFilter] = useState('all');
    const [termLines, setTermLines] = useState([{ ts: '--:--:--', text: 'SnapAgent LIVE Terminal initialized...', cls: 'text-cyan-400' }]);
    const [termCollapsed, setTermCollapsed] = useState(true);
    const eventTimestampsRef = useRef<number[]>([]);
    const [eps, setEps] = useState('0.0');

    // Search state
    const [searchQuery, setSearchQuery] = useState('A프로젝트 2층 평면도');
    const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
    const [searchTime, setSearchTime] = useState(0);
    const [searching, setSearching] = useState(false);
    const [searchDone, setSearchDone] = useState(false);

    // Agent state
    const [agentTask, setAgentTask] = useState('강남사옥 인테리어 프로젝트 현황 분석하고 보고서 작성해줘');
    const [workers, setWorkers] = useState<WorkerState[]>([]);
    const [masterStatus, setMasterStatus] = useState('대기 중');
    const [agentRunning, setAgentRunning] = useState(false);

    // Price state
    const [pName, setPName] = useState('강남사옥 인테리어');
    const [pArea, setPArea] = useState(320);
    const [pUsage, setPUsage] = useState('office');
    const [pGrade, setPGrade] = useState('premium');
    const [estimate, setEstimate] = useState<AIEstimate | null>(null);
    const [priceLoading, setPriceLoading] = useState(false);

    // Chat state
    const [chatOpen, setChatOpen] = useState(false);
    const [keyModal, setKeyModal] = useState(false);
    const [accessKey, setAccessKey] = useState('');
    const [keyVerified, setKeyVerified] = useState(false);
    const [chatSessionId, setChatSessionId] = useState<string | null>(null);
    const [expSessionId, setExpSessionId] = useState<string | null>(null);
    const defaultChat: ChatMessage[] = [
        { role: 'bot', content: '안녕하세요! Desinsight RAG 챗봇입니다.\n\n 예시: "A프로젝트 진행 상황", "전체 프로젝트 목록", "도서실 프로젝트"', timestamp: new Date().toISOString() },
    ];
    const [chatMessages, setChatMessages] = useState<ChatMessage[]>(defaultChat);

    useEffect(() => {
        try {
            const saved = localStorage.getItem('rag-chat-history');
            if (saved) setChatMessages(JSON.parse(saved));
        } catch { }
    }, []);
    const [chatLoading, setChatLoading] = useState(false);
    const chatEndRef = useRef<HTMLDivElement>(null);
    const termRef = useRef<HTMLDivElement>(null);
    const globalChatRef = useRef<HTMLInputElement>(null);

    // 대화 히스토리 localStorage 저장
    useEffect(() => {
        if (chatMessages.length > 1) {
            try { localStorage.setItem('rag-chat-history', JSON.stringify(chatMessages.slice(-50))); } catch { }
        }
    }, [chatMessages]);

    // Resizable panel widths
    const [leftPanelWidth, setLeftPanelWidth] = useState(240);
    const [rightPanelWidth, setRightPanelWidth] = useState(420);
    const containerRef = useRef<HTMLDivElement>(null);
    const isDraggingRef = useRef<'left' | 'right' | null>(null);
    const startXRef = useRef(0);
    const startWidthRef = useRef(0);

    // Resize handlers
    const handleResizeStart = useCallback((e: React.MouseEvent, side: 'left' | 'right') => {
        e.preventDefault();
        isDraggingRef.current = side;
        startXRef.current = e.clientX;
        startWidthRef.current = side === 'left' ? leftPanelWidth : rightPanelWidth;
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';

        const handleMouseMove = (ev: MouseEvent) => {
            if (!isDraggingRef.current || !containerRef.current) return;
            const containerWidth = containerRef.current.offsetWidth;
            const delta = ev.clientX - startXRef.current;
            if (isDraggingRef.current === 'left') {
                const newW = Math.max(160, Math.min(400, startWidthRef.current + delta));
                setLeftPanelWidth(newW);
            } else {
                // Right panel: delta is inverted (drag right = smaller)
                const newW = Math.max(280, Math.min(containerWidth * 0.6, startWidthRef.current - delta));
                setRightPanelWidth(newW);
            }
        };

        const handleMouseUp = () => {
            isDraggingRef.current = null;
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };

        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);
    }, [leftPanelWidth, rightPanelWidth]);

    // ── Helpers ─────────────────────────────────────────
    const addTermLine = useCallback((text: string, cls = 'text-cyan-400') => {
        const ts = new Date().toTimeString().slice(0, 8);
        setTermLines(prev => [...prev.slice(-(MAX_TERMINAL - 1)), { ts, text, cls }]);
    }, []);

    // ── Explorer (T-03: useExplorer 훅으로 분리) ──────────
    const {
        explorerAuth, explorerKeyInput, setExplorerKeyInput, explorerKeyCheck,
        dashStats, fileStats, nasOnline, nasCwd, nasItems, nasLoading, nasPath, loadNasDir,
        treeNodes, expandedDirs, treeFilter, setTreeFilter, toggleTreeDir, loadTreeRoot, setExpandedDirs,
        selectedFile, preview, previewLoading, loadPreview, downloadFile,
        uploading, uploadUnavailableReason, uploadFiles, fileInputRef, fmtSize, rightTab, setRightTab,
        expChat, expChatInput, setExpChatInput, expChatLoading, sendExpChat,
        expChatEndRef, expChatInputRef,
    } = useExplorer({ addTermLine });

    const updateEPS = useCallback(() => {
        const now = Date.now();
        eventTimestampsRef.current.push(now);
        eventTimestampsRef.current = eventTimestampsRef.current.filter(t => now - t < 10000);
        setEps((eventTimestampsRef.current.length / 10).toFixed(1));
    }, []);
    const formatTime = (iso: string) => new Date(iso).toTimeString().slice(0, 8);
    const fmtKRW = (n: number) => n.toLocaleString('ko-KR') + '원';

    // ── SSE (RAG 탭에서는 이벤트 처리 일시중지) ──────────
    const activeTabRef = useRef(activeTab);
    useEffect(() => { activeTabRef.current = activeTab; }, [activeTab]);

    useEffect(() => {
        let interval: ReturnType<typeof setInterval>;
        let isFetching = false;
        let disposed = false;
        const controller = new AbortController();

        const isRagTab = () => activeTabRef.current === 'rag-explorer';
        
        const fetchLiveData = async () => {
            if (isRagTab() || document.hidden || isFetching) return;
            
            isFetching = true;
            try {
                const res = await fetch('/api/live-stream', { signal: controller.signal, cache: 'no-store' });
                if (res.ok) {
                    const data = await res.json();
                    if (disposed) return;
                    setConnected(Boolean(data.observedAt));
                    if (data.observedAt) setLastObserved(data.observedAt);
                    
                    if (data.health) {
                        setNodeHealth(data.health);
                    }
                    
                    if (data.activities && data.activities.length > 0) {
                        const fresh = data.activities.filter((event: any) => {
                            const id = String(event.eventID || event.id || JSON.stringify([event.source || event.agent, event.timestamp || event.ts, event.content || event.message || event.text || event.action]));
                            if (seenActivities.current.has(id)) return false;
                            seenActivities.current.add(id); return true;
                        });
                        setEvents(p => [...fresh, ...p].slice(0, MAX_EVENTS));
                        setTotalEvents(p => p + fresh.length);
                        setAgentCounts(p => { const counts = { ...p }; fresh.forEach((event: any) => { counts[event.agent] = (counts[event.agent] || 0) + 1; }); return counts; });

                    }
                } else {
                    if (res.status === 401 || res.status === 503) window.dispatchEvent(new Event('live-session-expired'));
                    setConnected(false);
                }
            } catch (err) {
                setConnected(false);
            } finally {
                isFetching = false;
            }
        };

        // 초기 연결 및 폴링 루프 (7초 주기)
        fetchLiveData();
        interval = setInterval(fetchLiveData, 60000);

        const handleVisibility = () => {
            if (!document.hidden) {
                fetchLiveData();
            }
        };
        document.addEventListener('visibilitychange', handleVisibility);

        return () => {
            disposed = true; controller.abort();
            clearInterval(interval);
            document.removeEventListener('visibilitychange', handleVisibility);
            setConnected(false);
        };
    }, [updateEPS]);

    useEffect(() => { termRef.current?.scrollTo(0, termRef.current.scrollHeight); }, [termLines]);
    useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [chatMessages]);

    // ── Search (실제 RAG DB 검색 + 로그 + 필터) ──────────
    const [searchFilter, setSearchFilter] = useState('all');
    const [searchLogId, setSearchLogId] = useState('');
    const runSearch = async () => {
        if (!searchQuery.trim() || searching) return;
        setSearching(true); setSearchDone(false); setSearchResults([]); setSearchLogId('');
        const start = Date.now();
        const timerInterval = setInterval(() => setSearchTime((Date.now() - start) / 1000), 30);
        addTermLine(`[Search] "${searchQuery}" RAG DB 검색 중...`, 'text-yellow-400');
        try {
            let url = `/api/live/poby?endpoint=${encodeURIComponent('/api/search-files')}&q=${encodeURIComponent(searchQuery)}`;
            if (searchFilter !== 'all') url += `&filter=${searchFilter}`;
            const res = await fetch(url);
            clearInterval(timerInterval);
            const elapsed = (Date.now() - start) / 1000;
            setSearchTime(elapsed);
            if (!res.ok) throw new Error(`API Error: ${res.status}`);
            const data = await res.json();
            const results = data.results || [];
            setSearchResults(results);
            setSearching(false); setSearchDone(true);
            addTermLine(`[Search] "${searchQuery}" → ${data.total || 0} results in ${elapsed.toFixed(2)}s (실제 DB)`, 'text-cyan-400');

            // 검색 로그 DB 저장
            try {
                const logRes = await fetch('/api/search-log', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        query: searchQuery,
                        resultCount: results.length,
                        topResults: results.slice(0, 5).map((r: SearchResult) => ({ title: r.title, path: r.path, score: r.score })),
                        latency: elapsed,
                        filter: searchFilter !== 'all' ? searchFilter : null,
                    }),
                });
                const logData = await logRes.json();
                if (logData.id) setSearchLogId(logData.id);
            } catch { }
        } catch (e) {
            clearInterval(timerInterval);
            const elapsed = (Date.now() - start) / 1000;
            setSearchTime(elapsed);
            setSearchResults([]); setSearching(false); setSearchDone(true);
            addTermLine(`[Search] 검색 실패: ${e}`, 'text-red-400');
        }
    };

    const sendSearchFeedback = async (fb: 'up' | 'down') => {
        if (!searchLogId) return;
        try {
            await fetch('/api/search-log', {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', 'X-Live-Capability': 'gateway-chat' },
                body: JSON.stringify({ id: searchLogId, feedback: fb }),
            });
        } catch { }
    };

    const trackResultClick = async (idx: number) => {
        if (!searchLogId) return;
        try {
            await fetch('/api/search-log', {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', 'X-Live-Capability': 'gateway-chat' },
                body: JSON.stringify({ id: searchLogId, clickedIdx: idx }),
            });
        } catch { }
    };

    // ── Agent Task (실제 노드 헬스체크 기반) ───────────────
    const checkConnectionStatus = async () => {
        if (agentRunning) return;
        setAgentRunning(true);
 setMasterStatus(' 실제 노드 상태 수집 중...');
        setWorkers([]);
        addTermLine('[멀티에이전트] 9개 노드 실시간 헬스체크 시작', 'text-purple-400');

        try {
            // 실제 헬스체크 API 호출
            const res = await fetch(`/api/live/poby?endpoint=${encodeURIComponent('/api/nodes-health')}`);
            const healthData = await res.json();
            const nodes = healthData.nodes || [];

 setMasterStatus(` ${nodes.length}개 노드 응답 수신 — 분석 중...`);

            // 실제 노드 상태를 워커 카드로 변환
            const colors = ['border-purple-500', 'border-cyan-500', 'border-orange-500', 'border-blue-500', 'border-pink-500', 'border-green-500', 'border-yellow-500', 'border-red-500', 'border-indigo-500'];
 const EMOJI_MAP: Record<string, string>= { poby: '', jessi: '', dony: '', shon: '', tony: '', roony: '', rudy: '', meety: '', benny: '' };

            for (let i = 0; i < nodes.length; i++) {
                const n = nodes[i];
 const emoji = EMOJI_MAP[n.id] || '';
                const memStr = n.memory?.rss ? ` · ${Math.round(n.memory.rss / 1024 / 1024)}MB` : '';
                const uptimeStr = n.uptime ? ` · Up ${Math.round(n.uptime / 3600)}h` : '';
                const taskDesc = n.online
                    ? `${n.service} — ${n.hw} (${n.latency}ms${memStr}${uptimeStr})`
 : ` 오프라인 — ${n.hw}`;
                const geneId = `R_${Date.now().toString(36)}_${n.id}`;

                // 순차적으로 워커 추가 (실시간 느낌)
                await new Promise(r => setTimeout(r, 300));
                setWorkers(p => [...p.filter(w => w.name !== n.name), {
                    name: n.name, emoji, task: taskDesc,
                    color: colors[i % colors.length],
                    status: 'idle', kind: n.service, geneId,
                }]);
 addTermLine(`[${emoji} ${n.name}] ${n.online ? '' : ''} ${taskDesc}`, n.online ? 'text-green-400' : 'text-red-400');
            }

            // 완료 처리
            await new Promise(r => setTimeout(r, 500));
            // Health observations do not establish task execution or completion.
            const online = nodes.filter((n: NodeHealth) => n.online).length;
 setMasterStatus(` 연결 상태 관측 — ${online}/${nodes.length} 온라인`);
            setAgentRunning(false);
            addTermLine(`[멀티에이전트] 헬스체크 완료: ${online}/${nodes.length} 노드 온라인`, 'text-green-400');
        } catch (e) {
 setMasterStatus(` 오류: ${e}`);
            setAgentRunning(false);
            addTermLine(`[멀티에이전트] 오류: ${e}`, 'text-red-400');
        }
    };

    // ── SnapPrice ───────────────────────────────────────
    const runEstimate = async () => {
        if (priceLoading) return;
        setPriceLoading(true);
        setEstimate(null);
        addTermLine(`[SnapPrice] ${pName} AI 견적 요청 중...`, 'text-yellow-400');
        const start = Date.now();
        try {
            const res = await fetch('/api/snap-price', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-Live-Capability': 'gateway-chat' },
                body: JSON.stringify({ projectName: pName, area: pArea, usage: pUsage, grade: pGrade }),
            });
            const data = await res.json();
            if (data.error) throw new Error(data.error);
            const elapsed = ((Date.now() - start) / 1000).toFixed(1);
            setEstimate({ ...data, benchmark: data.benchmark || '', confidence: data.confidence || 0.9 });
            addTermLine(`[SnapPrice] ${pName} AI 견적 완료 (${elapsed}s): ${fmtKRW(data.total)}`, 'text-green-400');
        } catch (e) {
            addTermLine(`[SnapPrice] 견적 오류: ${e}`, 'text-red-400');
        }
        setPriceLoading(false);
    };


    // ── Chat ────────────────────────────────────────────
    const handleKeySubmit = () => { setKeyVerified(true); setKeyModal(false); setChatOpen(true); };
    const handleChatOpen = () => { if (keyVerified) setChatOpen(!chatOpen); else setKeyModal(true); };
    const sendChat = async () => {
        const inputVal = globalChatRef.current?.value?.trim() || '';
        if (!inputVal || chatLoading) return;
        const msg = inputVal;
        if (globalChatRef.current) globalChatRef.current.value = '';
        setChatMessages(p => [...p, { role: 'user', content: msg, timestamp: new Date().toISOString() }]);
        setChatLoading(true);

        // 빈 봇 메시지를 먼저 추가하여 타이핑 효과 준비
        setChatMessages(p => [...p, { role: 'bot', content: '', timestamp: new Date().toISOString() }]);

        try {
            const r = await fetch('/api/live/poby?endpoint=/api/gateway-chat', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-Live-Capability': 'gateway-chat' },
                body: JSON.stringify({ message: msg, accessKey, sessionId: chatSessionId })
            });
            if (!r.body) throw new Error('No readable stream');
            const reader = r.body.getReader();
            const decoder = new TextDecoder();
            let fullText = '';
            let buffer = '';

            setChatLoading(false); // 스트리밍이 시작되면 로딩 인디케이터 숨김

            let rafPending = false;
            const flushText = () => {
                setChatMessages(p => {
                    const newP = [...p];
                    newP[newP.length - 1] = { ...newP[newP.length - 1], content: fullText };
                    return newP;
                });
                rafPending = false;
            };

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });
                let lines = buffer.split('\n');
                buffer = lines.pop() || '';
                for (let line of lines) {
                    if (line.startsWith('data: ')) {
                        const str = line.slice(6).trim();
                        if (str === '[DONE]') break;
                        if (!str) continue;
                        try {
                            const j = JSON.parse(str);
                            if (j.error) fullText += `\n[에러: ${j.error}]\n`;
                            else if (j.text) fullText += j.text;
                            // 세션 ID 추출
                            if (j.sessionId && !chatSessionId) setChatSessionId(j.sessionId);
                        } catch (e) { }
                    }
                }
                // UI 갱신을 requestAnimationFrame으로 throttle (16ms)
                if (!rafPending) {
                    rafPending = true;
                    requestAnimationFrame(flushText);
                }
            }
            // 스트리밍 완료 후 최종 갱신
            flushText();
        } catch {
            setChatMessages(p => {
                const newP = [...p];
                newP[newP.length - 1] = { ...newP[newP.length - 1], content: '네트워크 오류가 발생했습니다.' };
                return newP;
            });
        }
        setChatLoading(false);
    };

    // ── Derived ─────────────────────────────────────────
    const onlineCount = nodeHealth?.online ?? 0;
    const offlineCount = nodeHealth ? nodeHealth.total - nodeHealth.online : 9;
    const avgLatency = nodeHealth?.nodes ? Math.round(nodeHealth.nodes.filter(n => n.online).reduce((s, n) => s + n.latency, 0) / Math.max(1, onlineCount)) : 0;
    const maxCount = Math.max(1, ...Object.values(agentCounts));
    const filteredEvents = filter === 'all' ? events : events.filter(e => e.level === filter);
    const improvement = searchDone && searchTime > 0 ? Math.round(40 * 60 / searchTime) : 0;



    return (
        <div className={`min-h-screen ${isDark ? 'bg-[#0a0c10] text-white' : 'bg-gray-50 text-gray-900'}`}>
            {/* Ambient */}
            {isDark && <div className="fixed inset-0 pointer-events-none overflow-hidden">
                <div className="absolute w-[600px] h-[600px] -top-[10%] -left-[5%] rounded-full bg-[radial-gradient(circle,rgba(34,197,94,0.2),transparent_70%)] blur-[120px] animate-pulse" />
                <div className="absolute w-[500px] h-[500px] bottom-[10%] -right-[10%] rounded-full bg-[radial-gradient(circle,rgba(6,182,212,0.15),transparent_70%)] blur-[120px] animate-pulse" />
            </div>}

            {/* Navbar */}
            <nav className={`fixed top-0 left-0 right-0 z-50 backdrop-blur-xl border-b ${isDark ? 'bg-gray-950/90 border-white/10' : 'bg-white/90 border-gray-200'}`}>
                <div className="max-w-[1600px] mx-auto px-6 h-14 flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                        <Link href="/" className="flex items-center gap-2 group">
                            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-xs group-hover:scale-110 transition-transform">D</div>
                            <span className={`text-base font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Desinsight</span>
                        </Link>
                        <span className="text-gray-600 font-light">/</span>
                        <span className="text-sm font-semibold text-green-400 tracking-wider">SnapAgent LIVE</span>
                    </div>
                    <div className="hidden md:flex items-center gap-1">
                        {TABS.map(tb => {
                            const Icon = TAB_ICONS[tb.icon];
                            return (
                            <button key={tb.id} onClick={() => setActiveTab(tb.id)}
                                className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg transition-all cursor-pointer ${activeTab === tb.id ? 'bg-indigo-500/15 border border-indigo-500/30 text-indigo-300' : isDark ? 'text-gray-500 hover:text-white hover:bg-white/5' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'}`}>
                                {Icon && <Icon className="w-3.5 h-3.5" />}
                                {tb.label}
                            </button>
                            );
                        })}
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="hidden sm:block">
                            <PipelineInbox />
                        </div>
                        <button onClick={toggleTheme} className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all cursor-pointer ${isDark ? 'bg-white/5 border border-white/10 hover:bg-white/10 text-yellow-400' : 'bg-gray-100 border border-gray-200 hover:bg-gray-200 text-blue-600'}`} title={isDark ? 'Light mode' : 'Dark mode'}>
                            {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                        </button>
                        <button onClick={toggleFullscreen} className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all cursor-pointer ${isDark ? 'bg-white/5 border border-white/10 hover:bg-white/10 text-gray-400' : 'bg-gray-100 border border-gray-200 hover:bg-gray-200 text-gray-600'}`} title={isFullscreen ? '축소' : '전체화면'}>
                            {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
                        </button>
                        <div className={`flex items-center gap-1.5 text-[11px] font-bold tracking-wider uppercase ${connected ? 'text-green-400' : 'text-yellow-400'}`}>
                            <span className={`w-2 h-2 rounded-full ${connected ? 'bg-green-400 animate-pulse' : 'bg-yellow-400'}`} />
                            {connected ? 'LIVE' : '...'}
                        </div>
                        <span className={`text-xs font-mono px-2.5 py-0.5 rounded-full ${isDark ? 'text-gray-400 bg-white/5 border border-white/10' : 'text-gray-600 bg-gray-100 border border-gray-200'}`}>{totalEvents.toLocaleString()}</span>
                        <Link href="/cowork" className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border transition-all ${isDark ? 'bg-cyan-500/10 border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/20' : 'bg-cyan-50 border-cyan-200 text-cyan-700 hover:bg-cyan-100'}`}><Handshake className="w-3.5 h-3.5" /> Cowork</Link>
                        <button onClick={handleChatOpen} className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border transition-all cursor-pointer ${keyVerified ? 'bg-green-500/10 border-green-500/30 text-green-400' : isDark ? 'bg-white/5 border-white/10 text-gray-400 hover:bg-white/10' : 'bg-gray-100 border-gray-200 text-gray-600 hover:bg-gray-200'}`}><MessageSquareText className="w-3.5 h-3.5" /> RAG</button>
                    </div>
                </div>
            </nav>

            <main className="relative z-10 max-w-[1600px] mx-auto px-6 pt-[72px] pb-10">
                {/* Hero */}
                <div className={`flex flex-wrap items-center justify-between py-4 mb-4 border-b ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                    <div>
                        <h1 className="text-2xl font-black tracking-tight flex items-center gap-3">
                            <span className="w-3 h-3 rounded-full bg-green-400 shadow-[0_0_12px_theme(colors.green.400)] animate-pulse" />
                            SnapAgent <span className="bg-gradient-to-r from-green-400 to-cyan-400 bg-clip-text text-transparent">LIVE</span>
                        </h1>
                        <p className="text-xs text-gray-500 mt-1">9-Node AI Agent Grid — Real-time Console</p>
                    </div>
                    <div className="flex gap-5 mt-2 sm:mt-0">
                        {[{ l: 'ONLINE', v: onlineCount }, { l: 'OFFLINE', v: offlineCount }, { l: 'AVG ms', v: avgLatency || '--' }, { l: 'EVT/s', v: eps }].map(s => (
                            <div key={s.l} className="text-center">
                                <div className="text-xl font-extrabold font-mono bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">{s.v}</div>
                                <div className="text-[10px] text-gray-500 tracking-wider font-semibold">{s.l}</div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Mobile Tabs */}
                <div className="flex md:hidden gap-1 mb-4 overflow-x-auto">
                    {TABS.map(t => (
                        <button key={t.id} onClick={() => setActiveTab(t.id)}
                            className={`text-xs font-semibold px-3 py-1.5 rounded-lg whitespace-nowrap transition-all cursor-pointer ${activeTab === t.id ? 'bg-indigo-500/15 border border-indigo-500/30 text-indigo-300' : 'text-gray-500 border border-transparent'}`}>
                            {t.label}
                        </button>
                    ))}
                </div>

                {/* ═══ TAB: STREAM ═══ */}
                {activeTab === 'stream' && (
                    <div className="space-y-4 mb-4">
                        {/* Company Overview — 기업 한눈에 */}
                        <div className={`grid grid-cols-2 md:grid-cols-4 gap-3`}>
                            {[
                                { label: '진행 프로젝트', value: '12', unit: '개', color: 'from-blue-500 to-cyan-500', sub: '203개 전체' },
                                { label: 'NAS 파일', value: '47K', unit: '건', color: 'from-purple-500 to-pink-500', sub: '3.2TB 사용중' },
                                { label: 'AI 분석', value: totalEvents.toLocaleString(), unit: '회', color: 'from-green-500 to-emerald-500', sub: '이번 세션' },
                                { label: '검색 절감', value: improvement > 0 ? `${improvement}×` : '--', unit: '', color: 'from-amber-500 to-orange-500', sub: '40분→AI' },
                            ].map(kpi => (
                                <div key={kpi.label} className={`rounded-xl px-4 py-3.5 border ${isDark ? 'bg-white/[0.03] border-white/[0.08]' : 'bg-white border-gray-200 shadow-sm'}`}>
                                    <div className="text-[10px] text-gray-500 font-semibold uppercase tracking-wider mb-1">{kpi.label}</div>
                                    <div className="flex items-baseline gap-1">
                                        <span className={`text-2xl font-black bg-gradient-to-r ${kpi.color} bg-clip-text text-transparent`}>{kpi.value}</span>
                                        {kpi.unit && <span className="text-xs text-gray-500">{kpi.unit}</span>}
                                    </div>
                                    <div className="text-[10px] text-gray-600 mt-0.5">{kpi.sub}</div>
                                </div>
                            ))}
                        </div>
                    <div className="grid grid-cols-[260px_1fr_300px] gap-4 max-xl:grid-cols-1">
 <Panel icon="" title="Node Status (Heartbeat)">
                            <div className="p-2 space-y-1 overflow-y-auto max-h-[460px]">
                                {AGENTS_MAP.map(n => {
                                    const h = nodeHealth?.nodes.find(x => x.id === n.id); const on = h?.online ?? false; return (
                                        <div key={n.id} className={`flex flex-col gap-1.5 px-3 py-2 rounded-lg bg-gray-50 dark:bg-white/[0.02] border ${on ? 'border-l-[3px] border-l-green-400 border-white/5' : 'border-l-[3px] border-l-red-400 border-white/5 opacity-60'}`}>
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-lg">{n.emoji}</span>
                                                    <div>
                                                        <div className="text-[12px] font-bold leading-none mb-0.5">{n.name}</div>
                                                        <div className="text-[9px] text-gray-400 truncate">{n.role}</div>
                                                    </div>
                                                </div>
                                                <span className={`text-[10px] font-mono font-semibold px-2 py-0.5 rounded-full ${on ? 'text-green-500 bg-green-500/10' : 'text-red-500 bg-red-500/10'}`}>{on ? (h?.service === 'heartbeat' ? 'HEARTBEAT' : `${h?.latency}ms`) : 'OFF'}</span>
                                            </div>
                                            {(h?.cpu != null || h?.memory != null) && (
                                                <div className="flex items-center gap-3 mt-1 pt-1 border-t border-gray-200 dark:border-white/5 pl-1 text-[9px] font-mono text-gray-500 dark:text-gray-400">
                                                    <div className="flex items-center gap-1" title="CPU Usage">
                                                        <Zap className="w-2.5 h-2.5 text-yellow-500 border-yellow-500" />
                                                        <span>{h?.cpu == null ? '미측정' : `${h.cpu}%`}</span>
                                                    </div>
                                                    <div className="flex items-center gap-1" title="RAM Usage">
                                                        <Database className="w-2.5 h-2.5 text-blue-500" />
                                                        <span>{typeof h?.memory === 'number' ? `${h.memory}%` : '미측정'}</span>
                                                    </div>
                                                    <div className="flex items-center gap-1" title="Active Tasks">
                                                        <Activity className="w-2.5 h-2.5 text-pink-500" />
                                                        <span>{h?.activeTasks || 0}</span>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </Panel>
 <Panel icon="" title="ACTIVITY LOG · JSON polling" className="flex flex-col" actions={['all', 'info', 'success', 'warn'].map(f =>(
                            <button key={f} onClick={() => setFilter(f)} className={`text-[11px] font-semibold px-2.5 py-0.5 rounded-full border cursor-pointer ${filter === f ? 'text-indigo-600 border-indigo-500 bg-indigo-500/10' : isDark ? 'text-gray-500 border-white/10 hover:bg-white/5' : 'text-gray-500 border-gray-300 hover:bg-gray-100'}`}>{f.charAt(0).toUpperCase() + f.slice(1)}</button>
                        ))}>
                            <div className="flex-1 h-[480px] max-xl:h-[360px] overflow-y-auto p-2 space-y-0.5">
 {filteredEvents.length === 0 && <div className="flex flex-col items-center justify-center h-full gap-3 text-gray-600"><span className="text-5xl opacity-30"><Radio className="w-4 h-4 inline-block" /></span><span className="text-sm">Waiting...</span></div>}
                                {filteredEvents.map((ev, i) => (
                                    <div key={`${ev.timestamp}-${i}`} className={`flex items-start gap-2.5 px-3 py-2 rounded-lg border-l-[3px] ${isDark ? 'hover:bg-white/[0.03]' : 'hover:bg-gray-50'} ${ev.level === 'info' ? 'border-l-indigo-500' : ev.level === 'success' ? 'border-l-green-500' : 'border-l-yellow-500'}`}>
                                        <span className="text-lg mt-0.5">{ev.emoji}</span>
                                        <div className="flex-1 min-w-0"><div className={`text-xs font-bold ${isDark ? '' : 'text-gray-900'}`}>{ev.agent}</div><div className={`text-xs mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{ev.action}</div></div>
                                        <div className="flex items-center gap-2 flex-shrink-0 text-[11px] font-mono"><span className={isDark ? 'text-cyan-400' : 'text-cyan-600'}>{ev.latency}ms</span><span className="text-gray-500">{formatTime(ev.timestamp)}</span></div>
                                    </div>
                                ))}
                            </div>
                        </Panel>
 <Panel icon="" title="Agent Activity">
                            <div className="p-4 space-y-2.5">
                                {AGENTS_MAP.map(n => {
                                    const c = agentCounts[n.name] || 0; return (
                                        <div key={n.id}><div className="flex justify-between mb-1"><span className="text-xs font-semibold flex items-center gap-1.5"><span className="text-sm">{n.emoji}</span>{n.name}</span><span className="text-[11px] font-mono text-gray-500">{c}</span></div>
                                            <div className={`h-1.5 rounded-full overflow-hidden ${isDark ? 'bg-white/[0.06]' : 'bg-gray-200'}`}><div className="h-full rounded-full bg-gradient-to-r from-blue-500 to-purple-500 transition-all duration-500" style={{ width: `${Math.round((c / maxCount) * 100)}%` }} /></div></div>
                                    );
                                })}
                            </div>
                        </Panel>
                    </div>
                    </div>
                )}

                {/* ═══ TAB: SEARCH ═══ */}
                {activeTab === 'search' && (
                    <div className="grid grid-cols-[1fr_auto_1.3fr] gap-6 max-lg:grid-cols-1 mb-4">
                        <Panel icon="" title="기존 수동 검색" className="opacity-60">
                            <div className="p-6 text-center">
                                <div className="text-5xl font-black font-mono text-red-400">~40<span className="text-lg font-medium text-gray-400 ml-1">분</span></div>
                                <div className="mt-4 space-y-2 text-sm text-gray-400 text-left">
                                    {['NAS 3TB+ 파일 검색 불가', '과거 도면 찾기 평균 40분', '퇴사자 자료 유실', '내역서↔도면 수동 대조'].map(t => (
 <div key={t} className="flex items-center gap-2"><span className="text-red-400 font-bold"></span>{t}</div>
                                    ))}
                                </div>
                            </div>
                        </Panel>
                        <div className="flex flex-col items-center justify-center py-10 max-lg:py-4 max-lg:flex-row">
                            <div className={`w-12 h-12 rounded-full flex items-center justify-center font-extrabold text-sm ${isDark ? 'bg-white/5 border border-white/10 text-gray-500' : 'bg-gray-100 border border-gray-300 text-gray-400'}`}>VS</div>
                            <div className={`w-px h-24 mt-2 max-lg:hidden ${isDark ? 'bg-gradient-to-b from-white/10 to-transparent' : 'bg-gradient-to-b from-gray-300 to-transparent'}`} />
                        </div>
 <Panel icon="" title="Desinsight AI 검색">
                            <div className="p-5">
                                <div className="flex gap-2 mb-2">
                                    <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} onKeyDown={e => e.key === 'Enter' && runSearch()}
                                        className={`flex-1 px-4 py-2.5 rounded-lg text-sm outline-none focus:border-indigo-500 ${isDark ? 'bg-white/5 border border-white/10 text-white' : 'bg-gray-50 border border-gray-300 text-gray-900'}`} placeholder="예: A프로젝트 2층 평면도" />
                                    <button onClick={runSearch} disabled={searching}
                                        className="px-5 py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg text-sm font-semibold text-white cursor-pointer disabled:opacity-50 hover:shadow-lg transition-all">AI 검색</button>
                                </div>
                                {/* 파일 유형 필터 */}
                                <div className="flex gap-1.5 mb-4 flex-wrap">
                                    {[
 { id: 'all', label: '전체', icon: '' },
 { id: 'dwg', label: 'DWG', icon: '' },
 { id: 'pdf', label: 'PDF', icon: '' },
 { id: 'excel', label: 'Excel', icon: '' },
 { id: 'doc', label: '회의록', icon: '' },
                                    ].map(f => (
                                        <button key={f.id} onClick={() => setSearchFilter(f.id)}
                                            className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-all cursor-pointer ${searchFilter === f.id
                                                ? 'bg-indigo-500/20 text-indigo-300 border border-indigo-500/30'
                                                : isDark ? 'bg-white/[0.04] text-gray-500 border border-white/[0.06] hover:text-gray-300' : 'bg-gray-100 text-gray-500 border border-gray-200 hover:text-gray-700'
                                                }`}>
                                            {f.icon} {f.label}
                                        </button>
                                    ))}
                                </div>
                                <div className="text-center mb-4">
                                    <span className="text-4xl font-black font-mono text-green-400">{searchTime.toFixed(2)}</span>
                                    <span className="text-sm text-gray-400 ml-1">초</span>
                                </div>
                                <div className="space-y-2">
                                    {searchResults.map((r, i) => (
                                        <div key={i} onClick={() => trackResultClick(i)} className={`p-3 rounded-lg transition-colors cursor-pointer ${isDark ? 'bg-white/[0.03] border border-white/[0.08] hover:border-cyan-500/30' : 'bg-gray-50 border border-gray-200 hover:border-cyan-500/50'}`}>
 <div className="flex justify-between items-start"><span className={`text-sm font-semibold ${isDark ? 'text-cyan-400' : 'text-cyan-700'}`}><FolderOpen className="w-4 h-4 inline-block" />{r.title}</span><span className={`text-[11px] font-mono px-2 py-0.5 rounded-full ${isDark ? 'bg-indigo-500/10 text-indigo-300' : 'bg-indigo-100 text-indigo-700'}`}>{Math.round(r.score * 100)}%</span></div>
 <div className="text-[11px] font-mono text-gray-500 mt-1"><Folder className="w-4 h-4 inline-block" />{r.path}</div>
                                            <div className="flex gap-3 mt-1.5 text-[10px]">
 <span className="text-purple-400"><Tag className="w-4 h-4 inline-block" />{r.id}</span>
 {r.files != null && <span className="text-gray-400"><FileText className="w-4 h-4 inline-block" />{r.files}파일</span>}
 {r.dwg != null && r.dwg >0 && <span className="text-orange-400"><Ruler className="w-4 h-4 inline-block" />DWG {r.dwg}</span>}
 {r.pdf != null && r.pdf >0 && <span className="text-red-400"><Files className="w-4 h-4 inline-block" />PDF {r.pdf}</span>}
                                                {r.size != null && r.size > 0 && <span className="text-gray-500">{(r.size / 1024 / 1024 / 1024).toFixed(1)}GB</span>}
                                                {r.year && <span className="text-blue-400">{r.year.replace('_PROJECT', '')}</span>}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                {/* 피드백 + 속도 개선 */}
                                {searchDone && searchResults.length > 0 && (
                                    <div className="flex items-center justify-between mt-4">
                                        <div className="flex gap-2">
 <button onClick={() =>sendSearchFeedback('up')} className={`px-3 py-1.5 rounded-lg text-xs font-medium cursor-pointer transition-all ${isDark ? 'bg-green-500/10 text-green-400 border border-green-500/20 hover:bg-green-500/20' : 'bg-green-50 text-green-600 border border-green-200 hover:bg-green-100'}`}><ThumbsUp className="w-4 h-4 inline-block" />유용해요</button>
 <button onClick={() =>sendSearchFeedback('down')} className={`px-3 py-1.5 rounded-lg text-xs font-medium cursor-pointer transition-all ${isDark ? 'bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20' : 'bg-red-50 text-red-600 border border-red-200 hover:bg-red-100'}`}><ThumbsDown className="w-4 h-4 inline-block" />아쉬워요</button>
                                        </div>
                                        {improvement > 0 && (
                                            <div className="text-right">
                                                <span className="text-2xl font-black bg-gradient-to-r from-green-400 to-cyan-400 bg-clip-text text-transparent">{improvement}×</span>
                                                <span className={`text-[10px] ml-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>속도 개선</span>
                                            </div>
                                        )}
                                    </div>
                                )}
                                {searchDone && searchResults.length === 0 && improvement > 0 && (
                                    <div className="mt-5 p-5 bg-gradient-to-r from-green-500/[0.06] to-cyan-500/[0.06] border border-green-500/20 rounded-xl text-center">
                                        <div className="text-5xl font-black bg-gradient-to-r from-green-400 to-cyan-400 bg-clip-text text-transparent">{improvement}×</div>
                                        <div className="text-sm text-gray-400 mt-1">속도 개선</div>
                                    </div>
                                )}
                            </div>
                        </Panel>
                    </div>
                )}

                {/* ═══ TAB: AGENTS ═══ */}
                {activeTab === 'agents' && (
                    <div className="mb-4">
                        <div className="flex gap-2 mb-6">
                            <input value={agentTask} onChange={e => setAgentTask(e.target.value)}
                                className={`flex-1 px-4 py-3 rounded-lg text-sm outline-none focus:border-indigo-500 ${isDark ? 'bg-white/5 border border-white/10 text-white' : 'bg-gray-50 border border-gray-300 text-gray-900'}`} placeholder="연결 상태 확인 (작업 실행 없음)" disabled />
                            <button onClick={checkConnectionStatus} disabled={agentRunning}
                                className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg text-sm font-semibold text-white cursor-pointer disabled:opacity-50 hover:shadow-lg transition-all">연결 상태 확인</button>
                        </div>
                        <div className="flex flex-col items-center gap-5">
                            <div className={`rounded-xl px-8 py-5 text-center ${isDark ? 'bg-white/[0.03] border border-indigo-500/30' : 'bg-indigo-50 border border-indigo-200'}`}>
                                <Network className={`w-8 h-8 mx-auto mb-2 ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`} />
                                <div className="text-base font-bold mt-1">Connection Status</div>
                                <div className={`text-xs font-mono mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{masterStatus}</div>
                            </div>
                            {workers.length > 0 && <div className="flex gap-1">{workers.map((_, i) => <div key={i} className="w-0.5 h-8 bg-gradient-to-b from-indigo-500 to-transparent" />)}</div>}
                            <div className="flex flex-wrap justify-center gap-4">
                                {workers.map(w => (
                                    <div key={w.name} className={`rounded-xl px-6 py-5 text-center min-w-[150px] transition-all ${isDark ? 'bg-white/[0.03]' : 'bg-white shadow-sm'} ${w.status === 'working' ? `${w.color} shadow-lg animate-pulse` : w.status === 'done' ? 'border border-green-500 shadow-[0_0_20px_theme(colors.green.500/0.15)]' : isDark ? 'border border-white/10' : 'border border-gray-200'}`}>
                                        <div className="text-2xl">{w.emoji}</div>
                                        <div className="text-sm font-bold mt-1">{w.name}</div>
                                        <div className={`text-[11px] mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{w.task}</div>
                                        <div className={`text-xs font-semibold mt-2 ${w.status === 'done' ? 'text-green-400' : w.status === 'working' ? 'text-yellow-400' : 'text-gray-500'}`}>
 연결 상태 관측
                                        </div>
                                    </div>
                                ))}
                            </div>
                            {workers.length > 0 && (
                                <Panel icon={<Activity className="w-4 h-4 text-purple-400" />} title="Connection observations" className="w-full mt-2">
                                    <div className="p-4 space-y-2 font-mono text-xs">
                                        <div className={`grid grid-cols-4 gap-2 font-semibold pb-2 border-b ${isDark ? 'text-gray-500 border-white/[0.08]' : 'text-gray-600 border-gray-200'}`}>
                                            <span>Observation ID</span><span>Kind</span><span>Status</span><span>Agent</span>
                                        </div>
                                        {workers.map(w => (
                                            <div key={w.geneId} className="grid grid-cols-4 gap-2 items-center">
                                                <span className="text-cyan-400 truncate">{w.geneId}</span>
                                                <span className="text-purple-400">{w.kind}</span>
                                                <span className={`px-2 py-0.5 rounded-full text-center text-[10px] font-semibold ${w.status === 'done' ? 'bg-green-500/15 text-green-400' : 'bg-yellow-500/15 text-yellow-400'}`}>연결 상태 관측</span>
                                                <span className="text-gray-400">{w.emoji} {w.name}: {w.task}</span>
                                            </div>
                                        ))}
                                    </div>
                                </Panel>
                            )}
                        </div>
                    </div>
                )}

                {/* ═══ TAB: PRICE ═══ */}
                {activeTab === 'price' && (
                    <div className="grid grid-cols-[1fr_1.5fr] gap-6 max-lg:grid-cols-1 mb-4">
 <Panel icon="" title="프로젝트 정보">
                            <div className="p-5 space-y-4">
                                <div><label className="text-[11px] text-gray-500 uppercase font-semibold tracking-wider">프로젝트명</label><input value={pName} onChange={e => setPName(e.target.value)} className={`w-full mt-1 px-3 py-2.5 rounded-lg text-sm outline-none focus:border-indigo-500 ${isDark ? 'bg-white/5 border border-white/10 text-white' : 'bg-gray-50 border border-gray-300 text-gray-900'}`} /></div>
                                <div><label className="text-[11px] text-gray-500 uppercase font-semibold tracking-wider">면적 (㎡)</label><input type="number" value={pArea} onChange={e => setPArea(Number(e.target.value))} className={`w-full mt-1 px-3 py-2.5 rounded-lg text-sm outline-none focus:border-indigo-500 ${isDark ? 'bg-white/5 border border-white/10 text-white' : 'bg-gray-50 border border-gray-300 text-gray-900'}`} /></div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div><label className="text-[11px] text-gray-500 uppercase font-semibold tracking-wider">용도</label>
                                        <select value={pUsage} onChange={e => setPUsage(e.target.value)} className={`w-full mt-1 px-3 py-2.5 rounded-lg text-sm outline-none focus:border-indigo-500 appearance-none ${isDark ? 'bg-white/5 border border-white/10 text-white' : 'bg-gray-50 border border-gray-300 text-gray-900'}`}>
                                            <option value="office">오피스</option><option value="retail">상업</option><option value="residential">주거</option><option value="education">교육</option>
                                        </select></div>
                                    <div><label className="text-[11px] text-gray-500 uppercase font-semibold tracking-wider">등급</label>
                                        <select value={pGrade} onChange={e => setPGrade(e.target.value)} className={`w-full mt-1 px-3 py-2.5 rounded-lg text-sm outline-none focus:border-indigo-500 appearance-none ${isDark ? 'bg-white/5 border border-white/10 text-white' : 'bg-gray-50 border border-gray-300 text-gray-900'}`}>
                                            <option value="premium">프리미엄</option><option value="standard">표준</option><option value="economy">경제</option>
                                        </select></div>
                                </div>
 <button onClick={runEstimate} disabled={priceLoading} className="w-full py-3 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg text-sm font-semibold text-white cursor-pointer hover:shadow-lg transition-all disabled:opacity-50">{priceLoading ? 'AI 분석 중...' : 'AI 견적 생성'}</button>
                            </div>
                        </Panel>
                        {estimate && (
 <Panel icon="" title="AI 견적서">
                                <div className="p-5">
                                    <div className={`p-3 rounded-lg text-sm mb-4 ${isDark ? 'bg-white/[0.03] text-gray-300' : 'bg-gray-50 text-gray-700 border border-gray-200'}`}>
                                        <strong className={isDark ? 'text-white' : 'text-gray-900'}>{pName}</strong> | {pArea}㎡ | <span className={isDark ? 'text-indigo-300 font-mono' : 'text-indigo-600 font-mono'}>{pUsage} · {pGrade}</span>
                                    </div>
                                    <table className="w-full text-sm">
                                        <thead><tr className={`text-[11px] uppercase tracking-wider border-b-2 ${isDark ? 'text-gray-500 border-white/10' : 'text-gray-500 border-gray-300'}`}>
                                            <th className="text-left py-2">항목</th><th className="text-right py-2">단가 (원/㎡)</th><th className="text-right py-2">수량</th><th className="text-right py-2">금액</th>
                                        </tr></thead>
                                        <tbody>{estimate.items.map(item => (
                                            <tr key={item.name} className={`border-b ${isDark ? 'border-white/[0.06]' : 'border-gray-200'}`}>
                                                <td className={`py-2 ${isDark ? '' : 'text-gray-900'}`}>{item.name}</td><td className={`text-right font-mono ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{item.unit.toLocaleString()}</td><td className={`text-right font-mono ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{item.area}</td><td className={`text-right font-mono ${isDark ? '' : 'text-gray-900'}`}>{item.amount.toLocaleString()}</td>
                                            </tr>
                                        ))}</tbody>
                                        <tfoot><tr className="border-t-2 border-indigo-500"><td colSpan={3} className="py-3 font-bold">합계 (VAT 별도)</td><td className="text-right font-bold text-lg text-indigo-300 font-mono">{fmtKRW(estimate.total)}</td></tr></tfoot>
                                    </table>
                                    <div className="mt-4 p-3 bg-indigo-500/[0.06] border border-indigo-500/20 rounded-lg text-xs text-gray-400 leading-relaxed">
                                        <div className="flex items-center gap-2 mb-2">
                                            <span className="text-indigo-300 font-semibold">AI 분석</span>
                                            <span className="px-2 py-0.5 bg-green-500/10 text-green-400 rounded-full text-[10px] font-mono">신뢰도 {Math.round(estimate.confidence * 100)}%</span>
                                        </div>
                                        <p>{estimate.analysis}</p>
                                        {estimate.benchmark && <p className="mt-1 text-purple-400">{estimate.benchmark}</p>}
                                    </div>
                                </div>
                            </Panel>
                        )}
                    </div>
                )}


                {/* ═══ TAB: RAG-EXPLORER ═══ */}
                {activeTab === 'rag-explorer' && !explorerAuth && (
                    <div className="mb-4">
 <Panel icon={<Lock className="w-4 h-4" />} title="RAG 탐색기 — 인증 필요">
                            <div className="p-10 flex flex-col items-center gap-5">
 <div className={`w-16 h-16 rounded-2xl flex items-center justify-center text-3xl ${isDark ? 'bg-indigo-500/10 border border-indigo-500/20' : 'bg-indigo-50 border border-indigo-200'}`}><Lock className="w-4 h-4 inline-block" /></div>
                                <h3 className={`text-lg font-bold ${isDark ? '' : 'text-gray-900'}`}>회사 내부 자료 접근</h3>
                                <p className={`text-sm text-center max-w-md ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>NAS 파일 탐색, 미리보기, RAG 챗봇은 승인 키가 필요합니다.</p>
                                <div className="flex gap-2 w-full max-w-sm">
                                    <input type="password" value={explorerKeyInput} onChange={e => setExplorerKeyInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && explorerKeyCheck()} placeholder="승인 키 입력..." className={`flex-1 px-4 py-3 rounded-lg text-sm outline-none focus:border-indigo-500 ${isDark ? 'bg-white/5 border border-white/10 text-white' : 'bg-gray-50 border border-gray-300 text-gray-900 placeholder-gray-400'}`} autoFocus />
                                    <button onClick={explorerKeyCheck} className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg text-sm font-semibold text-white cursor-pointer">확인</button>
                                </div>
                            </div>
                        </Panel>
                    </div>
                )}
                {activeTab === 'rag-explorer' && explorerAuth && (
                    <div className="flex flex-col gap-3">
                        {/* ── Top Bar: 통계 + 연도별 분포 ── */}
                        {dashStats?.summary && (
                            <div className="flex gap-3 items-stretch max-lg:flex-col">
                                <div className="flex gap-2 flex-1 max-sm:flex-wrap">
                                    {[
 { label: '프로젝트', value: dashStats.summary.cnt, unit: '개', icon: '', color: 'border-blue-400/30' },
 { label: '파일', value: (dashStats.summary.files / 1000).toFixed(0) + 'K', unit: '건', icon: '', color: 'border-emerald-400/30' },
 { label: '용량', value: (dashStats.summary.bytes / 1024 / 1024 / 1024).toFixed(1), unit: 'TB', icon: '', color: 'border-purple-400/30' },
 { label: 'DWG', value: (dashStats.summary.dwg / 1000).toFixed(1) + 'K', unit: '', icon: '', color: 'border-orange-400/30' },
 { label: 'PDF', value: (dashStats.summary.pdf / 1000).toFixed(1) + 'K', unit: '', icon: '', color: 'border-red-400/30' },
                                    ].map(s => (
                                        <div key={s.label} className={`flex items-center gap-2 px-3 py-1.5 backdrop-blur-sm border ${s.color} rounded-lg ${isDark ? 'bg-white/[0.06]' : 'bg-white shadow-sm'}`}>
                                            <span className="text-sm">{s.icon}</span>
                                            <span className={`font-bold text-sm ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{s.value}</span>
                                            <span className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>{s.unit || s.label}</span>
                                        </div>
                                    ))}
                                </div>
                                {dashStats?.byYear && dashStats.byYear.length > 0 && (
                                    <div className={`flex items-end gap-0.5 px-3 py-1 backdrop-blur-sm border rounded-lg min-w-[200px] ${isDark ? 'bg-white/[0.04] border-white/[0.08]' : 'bg-white border-gray-200 shadow-sm'}`}>
                                        {dashStats.byYear.map((y: { year_group: string; cnt: number; files: number }) => {
                                            const maxCnt = Math.max(...dashStats.byYear.map((v: { cnt: number }) => v.cnt));
                                            const pct = (y.cnt / maxCnt) * 100;
                                            return (
                                                <div key={y.year_group} className="flex-1 flex flex-col items-center gap-0.5 group" title={`${y.year_group}: ${y.cnt}개`}>
                                                    <div className="w-full bg-gradient-to-t from-indigo-500 to-blue-400 rounded-t-sm opacity-60 group-hover:opacity-100 transition-opacity min-h-[2px]" style={{ height: `${Math.max(pct * 0.3, 2)}px` }} />
                                                    <span className="text-[7px] text-gray-500 truncate w-full text-center">{y.year_group?.replace('_PROJECT', '')?.slice(-4)}</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                                
                                <div className={`flex gap-1.5 px-2 py-1.5 backdrop-blur-sm border rounded-lg ml-auto ${isDark ? 'bg-white/[0.04] border-white/[0.08]' : 'bg-white border-gray-200 shadow-sm'}`}>
                                    <button onClick={() => setShowExplorer(!showExplorer)} className={`px-3 py-1.5 rounded-md text-[11px] font-bold cursor-pointer transition flex items-center gap-1.5 ${showExplorer ? isDark ? 'bg-indigo-500/20 text-indigo-300' : 'bg-indigo-50 text-indigo-700' : isDark ? 'bg-white/5 text-gray-400' : 'bg-gray-50 text-gray-400'}`}>
                                        <FolderOpen className="w-3.5 h-3.5" /> 탐색기 {showExplorer ? '숨기기' : '보기'}
                                    </button>
                                    <button onClick={() => setShowAgent(!showAgent)} className={`px-3 py-1.5 rounded-md text-[11px] font-bold cursor-pointer transition flex items-center gap-1.5 ${showAgent ? isDark ? 'bg-purple-500/20 text-purple-300' : 'bg-purple-50 text-purple-700' : isDark ? 'bg-white/5 text-gray-400' : 'bg-gray-50 text-gray-400'}`}>
                                        <Bot className="w-3.5 h-3.5" /> 에이전트 {showAgent ? '숨기기' : '보기'}
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* ── Main 3-Panel Layout: Tree | Files | Preview/Chat ── */}
                        <div ref={containerRef} className={`flex gap-0 h-[calc(100vh-300px)] max-xl:flex-col max-xl:h-auto border rounded-xl overflow-hidden backdrop-blur-md ${isDark ? 'border-white/[0.1] bg-gray-900/60' : 'border-gray-200 bg-white shadow-lg'}`}>

                            {/* LEFT + CENTER (Explorer) */}
                            {showExplorer && (
                                <>
                                    {/* LEFT: Tree Sidebar (resizable) */}
                                    <div style={{ width: leftPanelWidth, minWidth: 160, maxWidth: 400 }} className={`max-xl:!w-full max-xl:h-48 border-r flex flex-col flex-shrink-0 ${isDark ? 'border-white/[0.08] bg-white/[0.03]' : 'border-gray-200 bg-gray-50'}`}>
                                <div className={`px-3 py-2 border-b flex items-center gap-2 ${isDark ? 'border-white/[0.06]' : 'border-gray-200'}`}>
                                    <span className={`text-[11px] font-semibold uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>탐색기</span>
                                    <span className="flex-1" />
                                    <button onClick={() => { loadTreeRoot(); loadNasDir(''); }} className={`text-xs cursor-pointer ${isDark ? 'text-gray-500 hover:text-gray-200' : 'text-gray-400 hover:text-gray-700'}`} title="새로고침">↻</button>
                                </div>
                                {/* Tree search filter */}
                                <div className={`px-2 py-1.5 border-b ${isDark ? 'border-white/[0.04]' : 'border-gray-200'}`}>
                                    <input value={treeFilter} onChange={e => setTreeFilter(e.target.value)} placeholder="폴더 검색..."
                                        className={`w-full px-2 py-1 text-[11px] rounded outline-none focus:border-indigo-500/50 ${isDark ? 'bg-white/[0.05] border border-white/[0.08] text-gray-200 placeholder-gray-600' : 'bg-white border border-gray-300 text-gray-800 placeholder-gray-400'}`} />
                                </div>
                                {/* Tree nodes */}
                                <div className="flex-1 overflow-y-auto py-1 select-none">
                                    {treeNodes.length === 0 && <div className="text-center py-4 text-gray-500 text-[11px]">로딩 중...</div>}
                                    {treeNodes
                                        .filter(n => !treeFilter || n.name.toLowerCase().includes(treeFilter.toLowerCase()))
                                        .map(node => {
                                            const isExpanded = expandedDirs.has(node.path);
                                            const isActive = nasCwd === node.path;
                                            return (
                                                <div key={node.path}>
                                                    <div onClick={() => toggleTreeDir(node.path)}
                                                        className={`flex items-center gap-1 px-2 py-[3px] cursor-pointer text-[11px] ${isDark ? 'hover:bg-white/[0.05]' : 'hover:bg-gray-100'} ${isActive ? (isDark ? 'bg-indigo-500/15 text-indigo-300' : 'bg-indigo-50 text-indigo-700') + ' font-medium' : isDark ? 'text-gray-400' : 'text-gray-700'}`}>
                                                        <span className="w-3 text-center text-[10px] text-gray-500">{isExpanded ? '▼' : '▶'}</span>
 <span className="text-xs">{isExpanded ? '' : ''}</span>
                                                        <span className="truncate flex-1">{node.name}</span>
                                                    </div>
                                                    {isExpanded && node.children && (
                                                        <div className={`ml-3 border-l ${isDark ? 'border-white/[0.06]' : 'border-gray-300'}`}>
                                                            {node.children.filter(c => c.type === 'dir').map(child => {
                                                                const childExpanded = expandedDirs.has(child.path);
                                                                const childActive = nasCwd === child.path;
                                                                return (
                                                                    <div key={child.path}>
                                                                        <div onClick={() => toggleTreeDir(child.path)}
                                                                            className={`flex items-center gap-1 px-2 py-[3px] cursor-pointer text-[11px] ${isDark ? 'hover:bg-white/[0.05]' : 'hover:bg-gray-100'} ${childActive ? (isDark ? 'bg-indigo-500/15 text-indigo-300' : 'bg-indigo-50 text-indigo-700') + ' font-medium' : isDark ? 'text-gray-400' : 'text-gray-700'}`}>
                                                                            <span className="w-3 text-center text-[10px] text-gray-500">{childExpanded ? '▼' : '▶'}</span>
 <span className="text-xs">{childExpanded ? '' : ''}</span>
                                                                            <span className="truncate flex-1">{child.name}</span>
                                                                        </div>
                                                                        {childExpanded && child.children && (
                                                                            <div className={`ml-3 border-l ${isDark ? 'border-white/[0.06]' : 'border-gray-300'}`}>
                                                                                {child.children.filter(gc => gc.type === 'dir').map(gc => (
                                                                                    <div key={gc.path} onClick={() => { loadNasDir(gc.path); setExpandedDirs(prev => new Set(prev).add(gc.path)); }}
                                                                                        className={`flex items-center gap-1 px-2 py-[3px] cursor-pointer text-[11px] ${isDark ? 'hover:bg-white/[0.05]' : 'hover:bg-gray-100'} ${nasCwd === gc.path ? (isDark ? 'bg-indigo-500/15 text-indigo-300' : 'bg-indigo-50 text-indigo-700') + ' font-medium' : isDark ? 'text-gray-400' : 'text-gray-700'}`}>
                                                                                        <span className="w-3" />
 <span className="text-xs"><Folder className="w-4 h-4 inline-block" /></span>
                                                                                        <span className="truncate flex-1">{gc.name}</span>
                                                                                    </div>
                                                                                ))}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                </div>
                            </div>

                            {/* LEFT RESIZE HANDLE */}
                            <div onMouseDown={e => handleResizeStart(e, 'left')}
                                className={`w-1.5 flex-shrink-0 cursor-col-resize group relative max-xl:hidden hover:w-2 transition-all ${isDark ? 'hover:bg-indigo-500/30' : 'hover:bg-indigo-200'}`}>
                                <div className={`absolute inset-y-0 left-0.5 w-0.5 transition-opacity opacity-0 group-hover:opacity-100 ${isDark ? 'bg-indigo-400/50' : 'bg-indigo-400'}`} />
                            </div>

                            {/* CENTER: File List */}
                            <div className={`flex-1 flex flex-col min-w-[200px] ${isDark ? '' : ''}`}>
                                {/* Breadcrumb bar */}
                                <div className={`px-3 py-2 border-b flex items-center gap-1.5 text-[11px] ${isDark ? 'border-white/[0.06] bg-white/[0.02]' : 'border-gray-200 bg-gray-50'}`}>
                                    <button onClick={() => loadNasDir('')} className="text-blue-400 hover:text-blue-300 cursor-pointer font-semibold flex items-center gap-1">
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" /></svg>
                                        ROOT
                                    </button>
                                    {nasPath.map((seg, i) => (
                                        <span key={i} className="flex items-center gap-1.5">
                                            <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className="text-gray-600"><path d="m9 18 6-6-6-6" /></svg>
                                            <button onClick={() => loadNasDir(nasPath.slice(0, i + 1).join('/'))}
                                                className={`cursor-pointer truncate max-w-[120px] ${i === nasPath.length - 1 ? (isDark ? 'text-gray-200' : 'text-gray-800') + ' font-semibold' : 'text-blue-400 hover:text-blue-300'}`}>{seg}</button>
                                        </span>
                                    ))}
                                    <span className="flex-1" />
                                    <span className={`${isDark ? 'text-gray-500' : 'text-gray-500'}`}>{fileStats.dirs}폴더 · {fileStats.files}파일</span>
                                    <button disabled title={uploadUnavailableReason}
                                        className={`ml-1 px-2 py-0.5 rounded text-[10px] font-semibold cursor-pointer transition-all disabled:opacity-40 ${isDark ? 'bg-indigo-500/15 border border-indigo-500/30 text-indigo-300 hover:bg-indigo-500/25' : 'bg-indigo-50 border border-indigo-200 text-indigo-600 hover:bg-indigo-100'}`}>
                                        업로드 불가
                                    </button>
                                    <input ref={fileInputRef} type="file" disabled multiple className="hidden" onChange={e => { if (e.target.files) uploadFiles(e.target.files); e.target.value = ''; }} />
                                </div>
                                <p role="status" className="text-xs px-3 py-2">{uploadUnavailableReason}</p>
                                {/* File list */}
                                <div className="flex-1 overflow-y-auto"
                                    onDragOver={e => { e.preventDefault(); e.stopPropagation(); }}
                                    onDrop={e => { e.preventDefault(); e.stopPropagation(); if (e.dataTransfer.files.length > 0) uploadFiles(e.dataTransfer.files); }}
                                >
                                    {nasLoading && <div className="flex items-center justify-center h-full text-gray-500 text-xs gap-2"><span className="animate-spin w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full inline-block"></span>로딩 중...</div>}
                                    {!nasLoading && nasItems.length === 0 && <div className="flex items-center justify-center h-full text-gray-500 text-xs">빈 디렉토리</div>}
                                    {!nasLoading && nasCwd && (
                                        <div onClick={() => { const parts = nasCwd.split('/').filter(Boolean); parts.pop(); loadNasDir(parts.join('/')); }}
                                            className={`flex items-center gap-2 px-3 py-1.5 cursor-pointer text-xs border-b ${isDark ? 'hover:bg-white/[0.04] border-white/[0.04]' : 'hover:bg-gray-50 border-gray-100'}`}>
                                            <span className="text-gray-500 text-sm">↩</span><span className="text-gray-500">..</span>
                                        </div>
                                    )}
                                    {/* Directories first, then files */}
                                    {!nasLoading && nasItems.filter(i => i.type === 'dir').map(item => (
                                        <div key={item.path}
                                            onClick={() => { loadNasDir(item.path); toggleTreeDir(item.path); }}
                                            className={`flex items-center gap-2.5 px-3 py-1.5 cursor-pointer text-xs border-b group ${isDark ? 'hover:bg-white/[0.04] border-white/[0.03]' : 'hover:bg-gray-50 border-gray-100'}`}>
 <span className="text-sm"><Folder className="w-4 h-4 inline-block" /></span>
                                            <span className={`flex-1 truncate font-medium ${isDark ? 'text-gray-300 group-hover:text-white' : 'text-gray-700 group-hover:text-gray-900'}`}>{item.name}</span>
                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-gray-600 opacity-0 group-hover:opacity-100"><path d="m9 18 6-6-6-6" /></svg>
                                        </div>
                                    ))}
                                    {!nasLoading && nasItems.filter(i => i.type === 'file').map(item => (
                                        <div key={item.path}
                                            onClick={() => loadPreview(item.path)}
                                            className={`flex items-center gap-2.5 px-3 py-1.5 cursor-pointer text-xs border-b group ${isDark ? 'hover:bg-white/[0.04] border-white/[0.03]' : 'hover:bg-gray-50 border-gray-100'} ${selectedFile === item.path ? (isDark ? 'bg-indigo-500/[0.1]' : 'bg-indigo-50') + ' border-l-2 border-l-indigo-400' : ''}`}>
                                            <span className="text-sm">{fileIcon(item.ext, item.type)}</span>
                                            <span className={`flex-1 truncate ${isDark ? 'group-hover:text-white' : 'group-hover:text-gray-900'} ${selectedFile === item.path ? (isDark ? 'text-indigo-300' : 'text-indigo-700') + ' font-medium' : isDark ? 'text-gray-400' : 'text-gray-600'}`}>{item.name}</span>
                                            <span className={`text-[10px] font-mono ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>{item.ext?.replace('.', '')}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* RIGHT RESIZE HANDLE */}
                            <div onMouseDown={e => handleResizeStart(e, 'right')}
                                className={`w-1.5 flex-shrink-0 cursor-col-resize group relative max-xl:hidden hover:w-2 transition-all ${isDark ? 'hover:bg-indigo-500/30' : 'hover:bg-indigo-200'}`}>
                                <div className={`absolute inset-y-0 left-0.5 w-0.5 transition-opacity opacity-0 group-hover:opacity-100 ${isDark ? 'bg-indigo-400/50' : 'bg-indigo-400'}`} />
                            </div>
                                </>
                            )}

                            {/* RIGHT: Preview + Chat (tabbed, resizable) */}
                            {showAgent && (
                                <div style={{ width: showExplorer ? rightPanelWidth : '100%', minWidth: 280, maxWidth: showExplorer ? '60%' : '100%' }} className="max-xl:!w-full max-xl:h-[500px] flex flex-col flex-shrink-0">
                                    {/* Tab bar */}
                                <div className={`flex border-b ${isDark ? 'border-white/[0.06] bg-white/[0.02]' : 'border-gray-200 bg-gray-50'}`}>
                                    {[
 { id: 'preview' as const, label: ' 미리보기', active: rightTab === 'preview' },
 { id: 'chat' as const, label: ' RAG 챗봇', active: rightTab === 'chat' },
                                    ].map(t => (
                                        <button key={t.id} onClick={() => setRightTab(t.id)}
                                            className={`flex-1 px-3 py-2 text-[11px] font-medium cursor-pointer transition-colors ${t.active ? (isDark ? 'text-white bg-white/[0.03]' : 'text-gray-900 bg-white') + ' border-b-2 border-indigo-500' : isDark ? 'text-gray-500 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'}`}>
                                            {t.label}
                                        </button>
                                    ))}
                                </div>

                                {/* Preview panel */}
                                {rightTab === 'preview' && (
                                    <div className="flex-1 overflow-y-auto p-4">
                                        {previewLoading && <div className="flex items-center justify-center h-full text-gray-500 text-sm gap-2"><span className="animate-spin w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full inline-block"></span>로딩 중...</div>}
                                        {!previewLoading && !preview && (
                                            <div className="flex flex-col items-center justify-center h-full gap-3 text-gray-500">
                                                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" className="opacity-20">
                                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                                    <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" />
                                                </svg>
                                                <p className="text-xs">파일을 선택하면 미리보기가 표시됩니다</p>
                                                <p className={`text-[10px] ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>PDF, PPT, XLS, DOC, HWP, CAD, SKP, 이미지 등 지원</p>
                                            </div>
                                        )}
                                        {!previewLoading && preview && preview.type === 'error' && (
                                            <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-xs text-red-400">{preview.error}</div>
                                        )}
                                        {!previewLoading && preview && preview.type === 'image' && (
                                            <div className="flex flex-col items-center gap-3">
                                                <img src={`data:${preview.mime};base64,${preview.data}`} alt={preview.name} className={`max-w-full max-h-[50vh] object-contain rounded-lg border ${isDark ? 'border-white/10' : 'border-gray-200'}`} />
                                                <div className="text-center">
                                                    <div className={`text-xs font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{preview.name}</div>
                                                    {preview.modified && <div className="text-[10px] text-gray-500">수정: {preview.modified}</div>}
                                                </div>
                                                <button onClick={() => selectedFile && downloadFile(selectedFile)}
                                                    className="px-3 py-1.5 text-[11px] font-semibold bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg cursor-pointer hover:opacity-90 flex items-center gap-1">
                                                    다운로드
                                                </button>
                                            </div>
                                        )}
                                        {!previewLoading && preview && preview.type === 'text' && (
                                            <div className="space-y-2">
                                                <div className="flex items-center gap-2 text-[11px] text-gray-500">
                                                    <span className="px-2 py-0.5 bg-indigo-500/10 border border-indigo-500/20 rounded text-indigo-300 font-mono">{preview.lang}</span>
                                                    <span>{preview.size?.toLocaleString()} bytes</span>
                                                    {preview.modified && <span>· {preview.modified}</span>}
                                                    <span className="flex-1" />
                                                    <button onClick={() => selectedFile && downloadFile(selectedFile)}
                                                        className="px-2 py-0.5 text-[10px] font-semibold bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded cursor-pointer hover:opacity-90">
                                                        다운로드
                                                    </button>
                                                </div>
                                                <pre className={`p-3 border rounded-lg text-[11px] overflow-x-auto whitespace-pre-wrap max-h-[50vh] overflow-y-auto font-mono leading-relaxed ${isDark ? 'bg-black/30 border-white/[0.06] text-gray-300' : 'bg-gray-50 border-gray-200 text-gray-800'}`}>{preview.content}</pre>
                                            </div>
                                        )}
                                        {!previewLoading && preview && preview.type === 'pdf' && (
                                            <div className="flex flex-col items-center gap-3 py-6">
 <div className="w-16 h-20 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center justify-center text-3xl"><FileText className="w-4 h-4 inline-block" /></div>
                                                <div className="text-center">
                                                    <div className={`font-semibold text-sm ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{preview.name}</div>
                                                    <div className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{preview.pages} 페이지{preview.size ? ` · ${fmtSize(preview.size)}` : ''}</div>
                                                </div>
                                                <button onClick={() => selectedFile && downloadFile(selectedFile)}
                                                    className="px-3 py-1.5 text-[11px] font-semibold bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg cursor-pointer hover:opacity-90 flex items-center gap-1">
                                                    다운로드
                                                </button>
                                            </div>
                                        )}
                                        {!previewLoading && preview && preview.type === 'cad' && (
                                            <div className="flex flex-col items-center gap-3 py-6">
 <div className="w-16 h-20 bg-orange-500/10 border border-orange-500/20 rounded-lg flex items-center justify-center text-3xl"><Ruler className="w-4 h-4 inline-block" /></div>
                                                <div className="text-center">
                                                    <div className={`font-semibold text-sm ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{preview.name}</div>
                                                    <div className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{preview.subtype}{preview.size ? ` · ${fmtSize(preview.size)}` : ''}</div>
                                                    {preview.modified && <div className="text-[10px] text-gray-500 mt-1">{preview.modified}</div>}
                                                </div>
                                                <button onClick={() => selectedFile && downloadFile(selectedFile)}
                                                    className="px-3 py-1.5 text-[11px] font-semibold bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg cursor-pointer hover:opacity-90 flex items-center gap-1">
                                                    다운로드
                                                </button>
                                            </div>
                                        )}
                                        {!previewLoading && preview && preview.type === 'office' && (
                                            <div className="flex flex-col items-center gap-3 py-6">
                                                <div className={`w-16 h-20 border rounded-lg flex items-center justify-center text-3xl ${preview.subtype?.includes('Excel') ? 'bg-emerald-500/10 border-emerald-500/20' : preview.subtype?.includes('PowerPoint') ? 'bg-orange-500/10 border-orange-500/20' : preview.subtype?.includes('HWP') || preview.subtype?.includes('한글') ? 'bg-sky-500/10 border-sky-500/20' : 'bg-blue-500/10 border-blue-500/20'}`}>
 {preview.subtype?.includes('Excel') ? '' : preview.subtype?.includes('PowerPoint') ? '' : preview.subtype?.includes('HWP') || preview.subtype?.includes('한글') ? '' : preview.subtype?.includes('Word') ? '' : ''}
                                                </div>
                                                <div className="text-center">
                                                    <div className={`font-semibold text-sm ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{preview.name}</div>
                                                    <div className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{preview.subtype}{preview.size ? ` · ${fmtSize(preview.size)}` : ''}</div>
                                                    {preview.modified && <div className="text-[10px] text-gray-500 mt-1">{preview.modified}</div>}
                                                </div>
                                                <button onClick={() => selectedFile && downloadFile(selectedFile)}
                                                    className="px-3 py-1.5 text-[11px] font-semibold bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg cursor-pointer hover:opacity-90 flex items-center gap-1">
                                                    다운로드
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Chat panel */}
                                {rightTab === 'chat' && (
                                    <div className="flex-1 flex flex-col min-h-0">
                                        <div className="flex-1 overflow-y-auto p-3 space-y-2">
                                            {expChat.map((m, i) => (
                                                <div key={i} className={`flex gap-2 ${m.role === 'user' ? 'flex-row-reverse' : ''}`}>
 <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs flex-shrink-0 ${m.role === 'bot' ? 'bg-indigo-500/20' : 'bg-white/10'}`}>{m.role === 'bot' ? '' : <User className="w-3 h-3" />}</div>
                                                    <div className={`max-w-[85%] px-3 py-2 text-xs leading-relaxed rounded-xl whitespace-pre-wrap ${m.role === 'bot' ? 'bg-white/[0.05] border border-white/[0.08] rounded-bl-sm text-gray-300' : 'bg-indigo-500/10 border border-indigo-500/20 rounded-br-sm text-gray-200'}`}>
                                                        {m.content || (expChatLoading && i === expChat.length - 1 ? (
                                                            <span className="flex gap-1"><span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" /><span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} /><span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} /></span>
                                                        ) : '')}
                                                    </div>
                                                </div>
                                            ))}
                                            {selectedFile && (
                                                <div className="px-2 py-1.5 bg-indigo-500/[0.06] border border-indigo-500/15 rounded-lg text-[10px] text-indigo-300">
 <Paperclip className="w-4 h-4 inline-block" />{selectedFile.split('/').pop()}
                                                </div>
                                            )}
                                            <div ref={expChatEndRef} />
                                        </div>
                                        <div className={`px-3 py-2 border-t flex gap-2 ${isDark ? 'border-white/[0.06]' : 'border-gray-200'}`}>
                                            <input ref={expChatInputRef} defaultValue="" onKeyDown={e => e.key === 'Enter' && !e.nativeEvent.isComposing && sendExpChat()}
                                                placeholder="프로젝트에 대해 질문..." className={`flex-1 px-2 py-1.5 text-xs rounded-lg outline-none focus:border-indigo-500 ${isDark ? 'bg-white/[0.05] border border-white/[0.08] text-gray-200 placeholder-gray-600' : 'bg-gray-50 border border-gray-300 text-gray-800 placeholder-gray-400'}`} />
                                            <button onClick={sendExpChat} disabled={expChatLoading}
                                                className="w-7 h-7 rounded-full bg-gradient-to-r from-blue-600 to-purple-600 flex items-center justify-center text-white cursor-pointer disabled:opacity-30">
                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m22 2-7 20-4-9-9-4z" /></svg>
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                        </div>

                        {/* ── Status Bar ── */}
                        <div className={`flex items-center gap-4 px-3 py-1.5 border rounded-lg text-[10px] ${isDark ? 'bg-white/[0.03] border-white/[0.06] text-gray-500' : 'bg-white border-gray-200 text-gray-500 shadow-sm'}`}>
                            <span className="flex items-center gap-1"><span className={`w-1.5 h-1.5 rounded-full ${nasOnline === false ? 'bg-yellow-500' : 'bg-green-500'}`} /> {nasOnline === false ? 'NAS 오프라인 (캐시)' : 'NAS 연결됨'}</span>
 <span><FolderOpen className="w-4 h-4 inline-block" />{nasCwd || '/'}</span>
                            <span>{fileStats.dirs} 폴더 · {fileStats.files} 파일</span>
 {selectedFile && <span className="text-indigo-400 truncate max-w-[300px]"><Paperclip className="w-4 h-4 inline-block" />{selectedFile}</span>}
                            <span className="flex-1" />
                            <span>grid.snapcodex.com</span>
                        </div>
                    </div>
                )}

                {/* ═══ TAB: DASHBOARD KPI ═══ */}
                {activeTab === 'dashboard' && (
                    <div className="mb-4">
                        <DashboardKPI isDark={isDark} onNavigateScheduler={() => setActiveTab('scheduler')} />
                    </div>
                )}

                {/* ═══ TAB: SCHEDULER ═══ */}
                {activeTab === 'scheduler' && (
                    <div className="mb-4">
                        <SchedulerTab isDark={isDark} isAuthenticated={true} />
                    </div>
                )}

                {/* ═══ TAB: DSLM INSIGHT ═══ */}
                {activeTab === 'insight' && (
                    <div className="mb-4 h-[700px]">
                        <DslmInsightPanel isDark={isDark} />
                    </div>
                )}

                {/* Terminal (always visible) */}
                <Panel icon={<Keyboard className="w-4 h-4" />} title="Terminal Log" className="mb-10" actions={
                    <div className="flex items-center gap-2">
                        <button onClick={() => setTermCollapsed(!termCollapsed)}
                            className={`text-[11px] font-semibold px-2.5 py-0.5 rounded border transition-colors cursor-pointer ${isDark ? 'border-white/10 text-gray-500 hover:text-indigo-400 hover:border-indigo-400' : 'border-gray-300 text-gray-500 hover:text-indigo-500 hover:border-indigo-500'}`}>{termCollapsed ? '▼ 펼치기' : '▲ 접기'}</button>
                        <button onClick={() => setTermLines([{ ts: new Date().toTimeString().slice(0, 8), text: 'Terminal cleared', cls: 'text-cyan-400' }])}
                            className={`text-[11px] font-semibold px-2.5 py-0.5 rounded border transition-colors cursor-pointer ${isDark ? 'border-white/10 text-gray-500 hover:text-red-400 hover:border-red-400' : 'border-gray-300 text-gray-500 hover:text-red-500 hover:border-red-500'}`}>Clear</button>
                    </div>
                }>
                    {!termCollapsed && (
                        <div ref={termRef} className={`h-40 overflow-y-auto px-4 py-3 font-mono text-xs leading-relaxed ${isDark ? 'bg-black/30' : 'bg-gray-900'}`}>
                            {termLines.map((l, i) => (<div key={i} className="flex gap-3"><span className="text-gray-600 flex-shrink-0">{l.ts}</span><span className={l.cls}>{l.text}</span></div>))}
                        </div>
                    )}
                    {termCollapsed && (
                        <div className={`px-4 py-2 text-xs font-mono ${isDark ? 'text-gray-600 bg-black/20' : 'text-gray-400 bg-gray-100'}`}>
                            터미널 로그 {termLines.length}줄 — 클릭하여 펼치기
                        </div>
                    )}
                </Panel>
            </main>

            <footer className={`relative z-10 border-t py-5 text-center text-sm ${isDark ? 'border-white/10 text-gray-600' : 'border-gray-200 text-gray-500'}`}>
                마지막 관측: {lastObserved || '미측정'} · {connected ? '관측 성공' : '오래됨 / 오류'} · &copy; 2026 Desinsight — SnapAgent LIVE v2026.2.28 &middot; ACTIVITY LOG · JSON polling &middot; 등록 노드
            </footer>

            {/* Key Modal */}
            {keyModal && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm">
                    <div className={`border rounded-2xl p-8 w-full max-w-md shadow-2xl ${isDark ? 'bg-gray-900 border-white/10' : 'bg-white border-gray-200'}`}>
 <h3 className={`text-xl font-bold mb-2 ${isDark ? '' : 'text-gray-900'}`}><Lock className="w-5 h-5 inline-block mr-1" /> RAG 챗봇 승인</h3>
                        <p className={`text-sm mb-6 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>회사 내부 자료 접근을 위한 승인 키를 입력하세요.</p>
                        <input type="password" value={accessKey} onChange={e => setAccessKey(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleKeySubmit()} placeholder="승인 키 입력..." className={`w-full px-4 py-3 rounded-lg text-sm outline-none focus:border-indigo-500 ${isDark ? 'bg-white/5 border border-white/10 text-white' : 'bg-gray-50 border border-gray-300 text-gray-900'}`} autoFocus />
                        <div className="flex gap-3 mt-6">
                            <button onClick={() => setKeyModal(false)} className={`flex-1 px-4 py-2.5 text-sm border rounded-lg cursor-pointer ${isDark ? 'text-gray-400 border-white/10 hover:bg-white/5' : 'text-gray-600 border-gray-300 hover:bg-gray-50'}`}>취소</button>
                            <button onClick={handleKeySubmit} className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg cursor-pointer">확인</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Chat Panel */}
            {chatOpen && keyVerified && (
                <div className={`fixed bottom-6 right-6 z-[90] w-[400px] max-h-[560px] backdrop-blur-xl border rounded-2xl shadow-2xl flex flex-col overflow-hidden ${isDark ? 'bg-gray-900/95 border-white/10' : 'bg-white/95 border-gray-200'}`}>
                    <div className={`px-5 py-3 border-b flex items-center justify-between ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
 <div className="flex items-center gap-2"><span className="text-lg"><Bot className="w-4 h-4 inline-block" /></span><span className={`font-bold text-sm ${isDark ? '' : 'text-gray-900'}`}>RAG 챗봇</span><span className="text-[10px] px-2 py-0.5 bg-green-500/10 text-green-400 rounded-full font-semibold">LIVE</span></div>
 <button onClick={() =>setChatOpen(false)} className={`text-lg cursor-pointer ${isDark ? 'text-gray-500 hover:text-white' : 'text-gray-400 hover:text-gray-900'}`}><XIcon className="w-4 h-4" /></button>
                    </div>
                    <div className="flex-1 overflow-y-auto p-4 space-y-3 max-h-[380px]">
                        {chatMessages.map((m, i) => (
                            <div key={i} className={`flex gap-2.5 ${m.role === 'user' ? 'flex-row-reverse' : ''}`}>
 <div className={`w-7 h-7 rounded-full flex items-center justify-center text-sm flex-shrink-0 ${m.role === 'bot' ? 'bg-indigo-500/20' : isDark ? 'bg-white/10' : 'bg-gray-100'}`}>{m.role === 'bot' ? '' : <User className="w-3.5 h-3.5" />}</div>
                                <div className={`max-w-[80%] px-3.5 py-2.5 text-sm leading-relaxed rounded-2xl ${m.role === 'bot' ? (isDark ? 'bg-white/5 border border-white/10' : 'bg-gray-50 border border-gray-200') + ' rounded-bl-sm' : (isDark ? 'bg-indigo-500/10 border border-indigo-500/20' : 'bg-indigo-50 border border-indigo-200') + ' rounded-br-sm'}`}>
                                    <div className={`whitespace-pre-wrap ${isDark ? '' : 'text-gray-800'}`}>{m.content}</div>
                                </div>
                            </div>
                        ))}
 {chatLoading && <div className="flex gap-2.5"><div className="w-7 h-7 rounded-full bg-indigo-500/20 flex items-center justify-center text-sm"><Bot className="w-4 h-4 inline-block" /></div><div className={`px-3.5 py-2.5 border rounded-2xl rounded-bl-sm flex gap-1 ${isDark ? 'bg-white/5 border-white/10' : 'bg-gray-50 border-gray-200'}`}><span className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" /><span className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} /><span className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} /></div></div>}
                        <div ref={chatEndRef} />
                    </div>
                    <div className={`px-4 py-3 border-t flex gap-2 ${isDark ? 'border-white/10 bg-gray-950/50' : 'border-gray-200 bg-gray-50'}`}>
                        <input ref={globalChatRef} defaultValue="" onKeyDown={e => e.key === 'Enter' && !e.nativeEvent.isComposing && sendChat()} placeholder="질문을 입력하세요..." className={`flex-1 px-3 py-2 rounded-lg text-sm outline-none focus:border-indigo-500 ${isDark ? 'bg-white/5 border border-white/10 text-white' : 'bg-white border border-gray-300 text-gray-900'}`} />
                        <button onClick={sendChat} disabled={chatLoading} className="w-9 h-9 rounded-full bg-gradient-to-r from-blue-600 to-purple-600 flex items-center justify-center text-white cursor-pointer disabled:opacity-50">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m22 2-7 20-4-9-9-4z" /></svg>
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
